import React from 'react';
import { useNavigate } from 'react-router-dom';
import { isIOSShell, isAndroidShell, signInWithAppleWeb } from './AuthUtils';
import { logAnalyticsEvent } from '../../utils/analytics';
import './AppleButton.css';

/**
 * "Continue with Apple" button on Login/Register.
 *
 * Two flows depending on where the app is running:
 * - iOS WKWebView shell: the native layer (WebView.swift) click-captures
 *   `.apple-signin-button`, runs the ASAuthorizationController flow, and calls
 *   window.handleAppleToken (AuthUtils.js). The onClick here is only a fallback
 *   in case the injected capture listener is absent.
 * - Any browser: Apple web auth via signInWithPopup (signInWithAppleWeb),
 *   using the Services ID configured in the Firebase Apple provider.
 *
 * Hidden inside the Android WebView shell: it only bridges Google natively and
 * force-routes `__/auth/` navigation to native Google Sign-In, which would
 * hijack the Apple web popup. Android intentionally stays Google + email
 * (no Guideline 4.8 equivalent on Play).
 *
 * Renders a bare <button>; sizing and stacking are owned by the parent
 * `.social-auth-buttons` column (see AppleButton.css).
 */
function AppleSignInButton({ setMessage }) {
  const navigate = useNavigate();

  if (isAndroidShell()) {
    return null;
  }

  const handleClick = () => {
    if (isIOSShell()) {
      // Normally unreachable: the shell's injected capture listener intercepts
      // the click before React and stops propagation.
      window.webkit.messageHandlers.iOSInterface.postMessage('appleSignInClicked');
      return;
    }
    logAnalyticsEvent('terms_acknowledged', { method: 'apple' });
    signInWithAppleWeb(navigate, setMessage);
  };

  return (
    <button
      type="button"
      className="apple-signin-button"
      onClick={handleClick}
    >
      <span className="apple-signin-button-icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 814 1000" aria-hidden="true">
          <path fill="#ffffff" d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
        </svg>
      </span>
      <span className="apple-signin-button-contents">Continue with Apple</span>
    </button>
  );
}

export default AppleSignInButton;
